import type {ReactNode} from 'react';
import {
  ArrowRight,
  BadgeCheck,
  CalendarDays,
  Disc3,
  Gamepad2,
  Library,
  ShoppingBag,
} from 'lucide-react';
import {EditorialAuthorLink} from './EditorialAuthor';

type PlayStationPhysicalMediaNewsPageProps = {
  header: ReactNode;
  footer: ReactNode;
};

const playStationAnnouncement =
  'https://blog.br.playstation.com/2026/07/01/producao-de-midias-fisicas-sera-encerrada-em-janeiro-de-2028-para-novos-jogos-lancados-em-consoles-playstation/';
const sonyInvestorRelations = 'https://www.sony.com/en/SonyInfo/IR/';
const sonyPositionUpdate =
  'https://www.gematsu.com/2026/07/sony-end-of-physical-game-disc-production-to-cautiously-move-forward-despite-strong-opinions';
const currentReaction =
  'https://www.techradar.com/gaming/sony-launches-new-it-happens-on-ps5-ad-featuring-gta-6-but-it-cant-escape-angry-fans-discs-used-to-happen-on-ps5';
const discDriveManual =
  'https://www.playstation.com/content/dam/global_pdc/en/corporate/support/manuals/accessories/ps5-accessories/disc-drive-cfi-zdd1/EN_FR_ES_PT_AMER_Disc_Drive_Instruction_Man_Web.pdf';

const ps5Offers = [
  {
    badge: 'PS5 com leitor',
    title: 'PlayStation 5 com unidade de disco',
    image: 'https://http2.mlstatic.com/D_NQ_NP_945456-MLA99456386724_112025-O.webp',
    description:
      'Para quem compra usados, troca jogos, empresta discos, tem biblioteca de PS4 ou também usa Blu-ray. Confirme o modelo e os itens do pacote na loja.',
    imageCredit: 'Imagem do produto no varejo. A apresentação pode variar conforme o anúncio.',
    links: [
      {label: 'Ver na Amazon', href: 'https://amzn.to/4omENQH'},
      {label: 'Outra oferta na Amazon', href: 'https://amzn.to/3PVgX1L'},
      {label: 'Ver no Mercado Livre', href: 'https://meli.la/1Td3Pji'},
    ],
  },
  {
    badge: 'PS5 Digital',
    title: 'PlayStation 5 Digital Edition',
    image: 'https://m.media-amazon.com/images/I/71WCygaQDAL._AC_SX679_.jpg',
    description:
      'Para uma biblioteca inteiramente digital e quando a economia inicial compensa perder revenda, empréstimo e comparação de preços entre discos.',
    imageCredit: 'Imagem do produto no varejo. A apresentação pode variar conforme o anúncio.',
    links: [
      {label: 'Ver na Amazon', href: 'https://amzn.to/49Pmy0j'},
      {label: 'Ver no Mercado Livre', href: 'https://meli.la/1pYjoaT'},
    ],
  },
  {
    badge: 'Acessório oficial',
    title: 'Unidade de disco para PS5 Slim Digital e PS5 Pro',
    image: '/guisite/ps5-disc-drive-mercadolivre-2x.webp',
    description:
      'Adiciona leitura de jogos em disco e Blu-ray aos modelos compatíveis. Não funciona no PS5 Digital original: confirme a série do console antes da compra. O primeiro pareamento exige conexão com a internet.',
    imageCredit: 'Fotografia real do produto disponibilizada no anúncio do Mercado Livre.',
    links: [
      {label: 'Ver na Amazon', href: 'https://link.amazon/B04wljsVM'},
      {label: 'Ver no Mercado Livre', href: 'https://meli.la/1kiZc7M'},
    ],
  },
];

export function PlayStationPhysicalMediaNewsPage({header, footer}: PlayStationPhysicalMediaNewsPageProps) {
  return (
    <main className="site-shell article-shell news-article-shell">
      {header}
      <article className="ps5-article news-article playstation-physical-media-news">
        <header className="article-hero">
          <div className="article-hero-copy">
            <a className="breadcrumb" href="/noticias">GuiaSite / Notícias / PlayStation</a>
            <p className="section-kicker">Decisão confirmada · impacto na compra</p>
            <h1>Sony mantém fim dos novos jogos em disco em 2028: PS5 com leitor ainda vale a pena?</h1>
            <p className="article-lead">
              A produção de discos para novos lançamentos PlayStation termina em janeiro de 2028,
              mas isso não inutiliza o leitor do PS5 nem apaga os jogos físicos já lançados. Entenda
              o que realmente muda, o que permanece e qual versão do console faz sentido comprar agora.
            </p>
            <div className="article-meta">
              <EditorialAuthorLink />
              <span><time dateTime="2026-08-22">Publicado em 22 de agosto de 2026</time></span>
              <span>Leitura útil: 10 min</span>
            </div>
          </div>
          <figure className="article-hero-media news-hero-media">
            <img
              loading="eager"
              src="/guisite/news-playstation-fim-midia-fisica-2028-premium.webp"
              alt="PlayStation 5 Slim com leitor e um disco em composição editorial sobre o fim das mídias físicas"
              width={1536}
              height={864}
              fetchPriority="high"
              decoding="async"
            />
            <figcaption>Composição editorial GuiaSite com referência oficial do PlayStation 5 Slim.</figcaption>
          </figure>
        </header>

        <div className="article-layout">
          <aside className="article-toc" aria-label="Índice da notícia">
            <strong>Nesta notícia</strong>
            <a href="#resposta-direta-discos">Resposta direta</a>
            <a href="#o-que-sony-anunciou">O anúncio da Sony</a>
            <a href="#o-que-nao-muda">O que não muda</a>
            <a href="#ps5-fisico-ou-digital-agora">Qual PS5 comprar</a>
            <a href="#ps6-e-midia-fisica">E o PS6?</a>
            <a href="#veredito-discos">Veredito</a>
            <a href="#faq-discos">Perguntas frequentes</a>
            <a href="#fontes-discos">Fontes</a>
          </aside>

          <div className="article-body">
            <section className="answer-card" id="resposta-direta-discos">
              <p className="section-kicker">Resposta direta</p>
              <h2>Sim, o PS5 com leitor ainda pode ser a melhor compra em 2026.</h2>
              <p>
                A decisão anunciada pela Sony vale para a <strong>produção de discos de novos jogos
                lançados a partir de janeiro de 2028</strong>. Ela não impede o PS5 de ler discos
                existentes, não afeta jogos físicos lançados antes desse prazo e não elimina a
                utilidade do leitor para mídia usada, biblioteca de PS4 compatível e filmes em Blu-ray.
              </p>
              <p>
                O modelo com leitor continua fazendo sentido para quem economiza comprando jogos
                físicos, vende depois de terminar ou compartilha discos. O PS5 Digital é mais coerente
                para quem já compra tudo online e encontra uma diferença de preço inicial realmente boa.
              </p>
              <div className="news-signal-row" aria-label="Informações principais">
                <span><CalendarDays size={18} /> Mudança em janeiro de 2028</span>
                <span><Disc3 size={18} /> Discos antigos continuam válidos</span>
                <span><ShoppingBag size={18} /> A escolha ainda afeta o custo total</span>
              </div>
            </section>

            <section id="o-que-sony-anunciou">
              <p className="section-kicker">O fato, sem exagero</p>
              <h2>O que a Sony anunciou sobre o fim da mídia física</h2>
              <p>
                Em 1º de julho de 2026, a PlayStation informou que deixará de produzir discos físicos
                para <strong>todos os novos jogos lançados em consoles PlayStation a partir de janeiro
                de 2028</strong>. Segundo o comunicado, esses lançamentos passarão a ser distribuídos
                em formato digital pela PlayStation Store e por varejistas parceiros.
              </p>
              <p>
                A reação da comunidade não fez a empresa revogar o plano. Na apresentação de resultados
                do primeiro trimestre fiscal de 2026, em 31 de julho, a diretoria reconheceu as opiniões
                fortes dos jogadores e afirmou que a transição seguiria com cautela. Em agosto, a discussão
                voltou a ganhar força nos comentários da nova campanha mundial do PS5.
              </p>
              <div className="news-fact-table" role="table" aria-label="Linha do tempo do fim dos novos jogos em disco no PlayStation">
                <div role="row"><strong>Data</strong><strong>O que aconteceu</strong><strong>Consequência prática</strong></div>
                <div role="row"><span>1 jul 2026</span><span>Anúncio oficial</span><span>Novos lançamentos deixam de receber disco em jan 2028</span></div>
                <div role="row"><span>31 jul 2026</span><span>Sony comenta a reação</span><span>Plano continua, com reconhecimento da resistência do público</span></div>
                <div role="row"><span>18 ago 2026</span><span>Debate volta à campanha do PS5</span><span>Tema segue relevante para compradores e colecionadores</span></div>
                <div role="row"><span>Jan 2028</span><span>Início da transição</span><span>Novos jogos passam a chegar apenas em formato digital</span></div>
              </div>
            </section>

            <section id="o-que-nao-muda">
              <p className="section-kicker">A distinção mais importante</p>
              <h2>O que não acaba em janeiro de 2028</h2>
              <div className="recommendation-grid">
                <div>
                  <Library aria-hidden="true" />
                  <strong>Sua coleção existente</strong>
                  <p>Jogos lançados em disco antes de janeiro de 2028 não são atingidos pelo encerramento anunciado.</p>
                </div>
                <div>
                  <Gamepad2 aria-hidden="true" />
                  <strong>O leitor do PS5</strong>
                  <p>O comunicado não diz que o hardware será desativado. O console continua lendo mídias compatíveis.</p>
                </div>
                <div>
                  <ShoppingBag aria-hidden="true" />
                  <strong>Compra e revenda de usados</strong>
                  <p>O mercado de discos já fabricados pode continuar existindo, embora a oferta futura dependa do varejo.</p>
                </div>
                <div>
                  <Disc3 aria-hidden="true" />
                  <strong>Blu-ray e jogos de PS4</strong>
                  <p>O leitor ainda preserva funções que a edição digital não oferece, observada a compatibilidade de cada mídia.</p>
                </div>
              </div>
              <div className="note-box">
                <BadgeCheck aria-hidden="true" />
                <div>
                  <strong>Não confunda fim de novos discos com bloqueio dos discos antigos.</strong>
                  <p>
                    A Sony delimitou a mudança pela data de lançamento do jogo. Dizer que “todo disco
                    deixa de funcionar em 2028” não é sustentado pelo anúncio oficial.
                  </p>
                </div>
              </div>
            </section>

            <section id="ps5-fisico-ou-digital-agora">
              <p className="section-kicker">Decisão de compra em 2026</p>
              <h2>PS5 físico ou digital depois do anúncio: quem deve escolher cada um?</h2>
              <div className="news-fact-table" role="table" aria-label="Comparação entre PS5 físico e digital após o anúncio de 2028">
                <div role="row"><strong>Seu perfil</strong><strong>Escolha mais coerente</strong><strong>Motivo</strong></div>
                <div role="row"><span>Compra jogos usados</span><span>PS5 com leitor</span><span>Permite comparar preços e revender depois</span></div>
                <div role="row"><span>Tem discos de PS4 compatíveis</span><span>PS5 com leitor</span><span>Preserva acesso à biblioteca sem recomprar digital</span></div>
                <div role="row"><span>Joga lançamentos e vende</span><span>PS5 com leitor</span><span>A revenda pode reduzir bastante o custo por jogo</span></div>
                <div role="row"><span>Só compra na PS Store</span><span>PS5 Digital</span><span>O leitor teria pouco uso no seu histórico real</span></div>
                <div role="row"><span>Assina PS Plus e joga catálogo</span><span>Depende da diferença de preço</span><span>Compare a economia inicial com sua biblioteca física</span></div>
                <div role="row"><span>Quer PS5 Pro com discos</span><span>Leitor separado compatível</span><span>Some o custo do acessório antes de decidir</span></div>
              </div>
              <p>
                O anúncio reduz o horizonte de novos lançamentos físicos, mas não apaga o valor econômico
                do acervo já disponível. Para quem costuma comprar um jogo usado e revendê-lo, o leitor pode
                se pagar antes de 2028. Para quem nunca fez isso, pagar mais pelo hardware apenas por receio
                da mudança também não é uma boa decisão.
              </p>
              <p>
                Nos modelos compatíveis, a unidade de disco oficial pode ser adicionada ao PS5 Slim Digital
                e ao PS5 Pro. Ela <strong>não é um acessório universal para qualquer PS5</strong>: confirme o
                grupo do modelo e a compatibilidade indicada pela Sony antes da compra.
              </p>
              <a className="text-link" href={discDriveManual} target="_blank" rel="noopener noreferrer">
                Consultar o manual oficial da unidade de disco <ArrowRight size={16} aria-hidden="true" />
              </a>
            </section>

            <section id="ps6-e-midia-fisica">
              <p className="section-kicker">Sem transformar hipótese em fato</p>
              <h2>O anúncio confirma que o PS6 será totalmente digital?</h2>
              <p>
                <strong>Não.</strong> A Sony não apresentou, nas fontes consultadas, o desenho final do
                próximo console nem confirmou se ele terá leitor interno, leitor removível ou alguma forma
                de compatibilidade física. O anúncio trata da produção de discos para novos jogos, não das
                especificações de um PS6.
              </p>
              <p>
                É razoável concluir que a distribuição digital terá papel dominante no futuro da plataforma.
                Não é responsável concluir, a partir disso, que todo disco de gerações anteriores ficará sem
                uso ou que um hardware ainda não detalhado oficialmente terá determinada configuração.
              </p>
            </section>

            <section id="veredito-discos" className="final-verdict">
              <p className="section-kicker">Veredito GuiaSite</p>
              <h2>O anúncio de 2028 não transforma o PS5 com leitor em uma compra ruim.</h2>
              <p>
                Compre o modelo com leitor se discos usados, revenda, empréstimo, coleção de PS4 ou Blu-ray
                fazem parte do seu uso. Escolha o Digital quando seu histórico já é inteiramente online e a
                diferença de preço do console compensa de verdade. O erro é pagar pelo leitor sem usá-lo ou
                abrir mão dele sem calcular quanto a liberdade de comprar fora da PlayStation Store pode economizar.
              </p>
            </section>

            <section className="affiliate-section" aria-label="Opções de PlayStation 5 e unidade de disco compatível">
              <div className="affiliate-heading">
                <p className="section-kicker">Depois do veredito</p>
                <h2>Compare PS5 com leitor, PS5 Digital e a unidade de disco</h2>
                <p>
                  Não exibimos preço fixo porque estoque, bundle, vendedor e forma de pagamento mudam. Abra
                  as lojas, confirme a versão exata e compare o custo total antes de fechar a compra. No caso
                  da unidade separada, verifique primeiro a série do seu PS5.
                </p>
              </div>
              <div className="affiliate-disclosure">
                <BadgeCheck size={18} aria-hidden="true" />
                <p>
                  Transparência editorial: os botões abaixo são links de afiliado e podem gerar comissão para
                  o GuiaSite, sem custo extra para você. Isso não altera o veredito.
                </p>
              </div>
              <div className="affiliate-grid">
                {ps5Offers.map((offer) => (
                  <article className="affiliate-card" key={offer.title}>
                    <div className="affiliate-image">
                      <img loading="lazy" src={offer.image} alt={offer.title} width={500} height={500} decoding="async" />
                    </div>
                    <div className="affiliate-copy">
                      <span>{offer.badge}</span>
                      <h3>{offer.title}</h3>
                      <p>{offer.description}</p>
                      <small>{offer.imageCredit}</small>
                      <div className="affiliate-actions">
                        {offer.links.map((link) => (
                          <a
                            key={link.href}
                            href={link.href}
                            target="_blank"
                            rel="nofollow sponsored noopener noreferrer"
                            aria-label={`${link.label}: ${offer.title}`}
                          >
                            {link.label} <ArrowRight size={15} aria-hidden="true" />
                          </a>
                        ))}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            <section id="faq-discos">
              <h2>Perguntas frequentes sobre o fim dos jogos em disco no PlayStation</h2>
              <div className="faq-list">
                <details>
                  <summary>Os discos do PS5 vão parar de funcionar em 2028?</summary>
                  <p>Não. A Sony anunciou o fim da produção de discos para novos lançamentos a partir de janeiro de 2028. Jogos físicos lançados antes dessa data não são afetados pela transição anunciada.</p>
                </details>
                <details>
                  <summary>A Sony desistiu do fim da mídia física após as críticas?</summary>
                  <p>Até 22 de agosto de 2026, não. A diretoria reconheceu a reação da comunidade, mas informou que o plano continuaria com cautela.</p>
                </details>
                <details>
                  <summary>Vale comprar PS5 com leitor em 2026?</summary>
                  <p>Sim, quando você compra usados, revende jogos, empresta discos, tem biblioteca física de PS4 compatível ou usa Blu-ray. Caso compre tudo digitalmente, compare a diferença de preço entre os consoles.</p>
                </details>
                <details>
                  <summary>O PS5 Digital pode receber leitor depois?</summary>
                  <p>Os modelos Slim compatíveis aceitam a unidade de disco oficial vendida separadamente. O PS5 Pro também foi projetado para esse acessório. Confira o código e o grupo do modelo antes de comprar.</p>
                </details>
                <details>
                  <summary>O PS6 será lançado sem leitor?</summary>
                  <p>Não há confirmação oficial suficiente para afirmar isso. A política de novos jogos digitais a partir de 2028 não revela, por si só, a configuração final do próximo console.</p>
                </details>
                <details>
                  <summary>É melhor comprar discos agora por medo de faltar?</summary>
                  <p>Não por pânico. Compre os jogos que pretende jogar ou colecionar e compare preços. O anúncio não determina escassez imediata dos títulos já existentes.</p>
                </details>
              </div>
            </section>

            <section id="fontes-discos">
              <p className="section-kicker">Transparência editorial</p>
              <h2>Fontes e atualização</h2>
              <p>
                Notícia publicada em 22 de agosto de 2026. O fato central e a exceção para jogos já
                lançados foram conferidos no comunicado oficial da PlayStation. A continuidade do plano
                foi verificada na apresentação de resultados da Sony e em cobertura que reproduz a fala
                da diretoria. A repercussão de agosto é contexto, não fundamento do veredito.
              </p>
              <ul className="source-list">
                <li><a href={playStationAnnouncement} target="_blank" rel="noopener noreferrer">PlayStation Blog Brasil: anúncio oficial sobre os novos jogos em disco</a></li>
                <li><a href={sonyInvestorRelations} target="_blank" rel="noopener noreferrer">Sony Investor Relations: resultados do primeiro trimestre fiscal de 2026</a></li>
                <li><a href={sonyPositionUpdate} target="_blank" rel="noopener noreferrer">Gematsu: posição da diretoria da Sony após a reação dos jogadores</a></li>
                <li><a href={currentReaction} target="_blank" rel="noopener noreferrer">TechRadar: repercussão renovada na campanha do PS5 em agosto</a></li>
                <li><a href={discDriveManual} target="_blank" rel="noopener noreferrer">PlayStation: manual oficial da unidade de disco CFI-ZDD1</a></li>
              </ul>
            </section>

            <section className="related-reading">
              <p className="section-kicker">Próxima decisão</p>
              <h2>Continue comparando antes de comprar</h2>
              <div className="recommendation-grid">
                <a href="/comparar/ps5-fisico-vs-digital/">
                  <strong>PS5 físico ou digital: qual escolher?</strong>
                  <p>Compare preço inicial, jogos usados, revenda e biblioteca física.</p>
                </a>
                <a href="/games/playstation-5/">
                  <strong>PS5 ainda vale a pena em 2026?</strong>
                  <p>Avalie biblioteca, vida útil e o custo de esperar pelo próximo console.</p>
                </a>
                <a href="/comparar/nintendo-switch-2-vs-ps5/">
                  <strong>Nintendo Switch 2 ou PS5?</strong>
                  <p>Escolha entre portabilidade, exclusivos, potência e custo total.</p>
                </a>
              </div>
            </section>
          </div>
        </div>
      </article>
      {footer}
    </main>
  );
}
